import { useState, useMemo } from "react";
import {
  Container,
  Box,
  Title,
  Text,
  Stack,
  Modal,
  Button,
  Group,
  Badge,
  MultiSelect,
  Paper,
  TextInput,
  Table,
  Pagination,
  ActionIcon,
  Tooltip,
  Loader,
  Center,
} from "@mantine/core";
import { useDisclosure, useDebouncedValue } from "@mantine/hooks";
import {
  IconSearch,
  IconPlus,
  IconEdit,
  IconFilter,
  IconRefresh,
  IconCheck,
  IconX,
} from "@tabler/icons-react";
import {
  useIcdCodesAdmin,
  useCreateIcdCode,
  useUpdateIcdCode,
  useToggleIcdCodeActive,
} from "../../../hooks/admin/useIcdCodes";
import IcdCodeForm, {
  IcdCodeFormValues,
} from "../../../components/admin/icdcodes/IcdCodeForm";
import { IcdCode } from "../../../services/admin/icdcodes.service";

interface IcdCodeFilters {
  statusFilter?: string[];
}

const ITEMS_PER_PAGE = 20;

export default function IcdCodesAdminPage() {
  // Modal states
  const [formModalOpened, { open: openFormModal, close: closeFormModal }] =
    useDisclosure(false);
  const [
    filterModalOpened,
    { open: openFilterModal, close: closeFilterModal },
  ] = useDisclosure(false);
  const [
    confirmModalOpened,
    { open: openConfirmModal, close: closeConfirmModal },
  ] = useDisclosure(false);

  // Page state
  const [editing, setEditing] = useState<IcdCode | null>(null);
  const [itemToToggle, setItemToToggle] = useState<IcdCode | null>(null);
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebouncedValue(search, 300);
  const [filters, setFilters] = useState<IcdCodeFilters>({});
  const [pendingFilters, setPendingFilters] = useState<IcdCodeFilters>({});
  const [page, setPage] = useState(1);

  // Data hooks
  const { data: icdCodes = [], isLoading, refetch } = useIcdCodesAdmin();
  const createIcdCode = useCreateIcdCode();
  const updateIcdCode = useUpdateIcdCode();
  const toggleIcdCodeActive = useToggleIcdCodeActive();

  // Filter & search data
  const filteredData = useMemo(() => {
    let result = icdCodes;

    // Text search
    if (debouncedSearch) {
      const lowerSearch = debouncedSearch.toLowerCase();
      result = result.filter(
        (item) =>
          item.code.toLowerCase().includes(lowerSearch) ||
          item.description.toLowerCase().includes(lowerSearch)
      );
    }

    // Status filter
    if (filters.statusFilter && filters.statusFilter.length > 0) {
      result = result.filter((item) => {
        const status = item.isActive ? "active" : "inactive";
        return filters.statusFilter!.includes(status);
      });
    }

    return result;
  }, [icdCodes, debouncedSearch, filters]);

  // Pagination
  const totalPages = Math.ceil(filteredData.length / ITEMS_PER_PAGE);
  const paginatedData = useMemo(() => {
    const start = (page - 1) * ITEMS_PER_PAGE;
    return filteredData.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredData, page]);

  // Check if any filters are active
  const hasActiveFilters =
    filters.statusFilter && filters.statusFilter.length > 0;

  // Handlers
  const handleAdd = () => {
    setEditing(null);
    openFormModal();
  };

  const handleEdit = (item: IcdCode) => {
    setEditing(item);
    openFormModal();
  };

  const handleSubmit = async (values: IcdCodeFormValues) => {
    try {
      if (editing) {
        await updateIcdCode.mutateAsync({
          id: editing.id,
          ...values,
        });
      } else {
        await createIcdCode.mutateAsync(values);
      }
      closeFormModal();
      setEditing(null);
    } catch {
      // Error already handled by mutation's onError callback
    }
  };

  const handleToggleActive = (item: IcdCode) => {
    setItemToToggle(item);
    openConfirmModal();
  };

  const confirmToggleActive = async () => {
    if (itemToToggle) {
      await toggleIcdCodeActive.mutateAsync({
        id: itemToToggle.id,
        isActive: !itemToToggle.isActive,
      });
      closeConfirmModal();
      setItemToToggle(null);
    }
  };

  const handleClearAllFilters = () => {
    setFilters({});
    setPendingFilters({});
    setPage(1);
  };

  const handleOpenFilterModal = () => {
    setPendingFilters({ ...filters });
    openFilterModal();
  };

  const handleApplyFilters = () => {
    setFilters({ ...pendingFilters });
    setPage(1);
    closeFilterModal();
  };

  const handleClearPendingFilters = () => {
    setPendingFilters({});
  };

  return (
    <Container fluid py="xl">
      <Box style={{ maxWidth: "1600px", margin: "0 auto" }}>
        <Stack gap="lg">
          {/* Header */}
          <Group justify="space-between" align="flex-start">
            <div>
              <Title order={2}>ICD Codes</Title>
              <Text size="sm" c="dimmed" mt="xs">
                Manage ICD diagnostic codes for clinical history and diagnoses
              </Text>
            </div>
            <Button leftSection={<IconPlus size={16} />} onClick={handleAdd}>
              Add New
            </Button>
          </Group>

          {/* Search and Filters Row */}
          <Group align="stretch" grow>
            {/* Search Section */}
            <Paper p="md" withBorder style={{ flex: 2 }}>
              <Stack gap="sm">
                <Group>
                  <IconSearch size={20} />
                  <Text fw={500}>Search</Text>
                </Group>
                <TextInput
                  placeholder="Search by code or description..."
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(1);
                  }}
                  leftSection={<IconSearch size={16} />}
                />
              </Stack>
            </Paper>

            {/* Filters Section */}
            <Paper p="md" withBorder style={{ flex: 1 }}>
              <Stack gap="sm">
                <Group justify="space-between">
                  <Group>
                    <IconFilter size={20} />
                    <Text fw={500}>Filters</Text>
                  </Group>
                  <Button
                    variant="subtle"
                    size="compact-sm"
                    onClick={() => refetch()}
                    loading={isLoading}
                    leftSection={<IconRefresh size={16} />}
                  />
                </Group>
                <Button
                  variant="light"
                  onClick={handleOpenFilterModal}
                  fullWidth
                >
                  {hasActiveFilters ? "Edit Filters" : "Add Filters"}
                </Button>
                {hasActiveFilters && (
                  <Group gap="xs" wrap="wrap">
                    <Text size="sm" c="dimmed">
                      Active:
                    </Text>
                    {filters.statusFilter &&
                      filters.statusFilter.length > 0 && (
                        <Badge variant="light" color="blue">
                          {filters.statusFilter.length} Status
                          {filters.statusFilter.length > 1 ? "es" : ""}
                        </Badge>
                      )}
                    <Button
                      variant="subtle"
                      size="compact-xs"
                      color="gray"
                      onClick={handleClearAllFilters}
                    >
                      Clear All
                    </Button>
                  </Group>
                )}
                <Text size="sm" c="dimmed">
                  {filteredData.length} code
                  {filteredData.length !== 1 ? "s" : ""} found
                </Text>
              </Stack>
            </Paper>
          </Group>

          {/* Table Section */}
          <Paper p="md" withBorder>
            <Stack gap="md">
              {/* Table Header with Pagination */}
              <Group justify="space-between">
                <Text fw={500} size="lg">
                  ICD Codes ({filteredData.length})
                </Text>
                {totalPages > 1 && (
                  <Group gap="md">
                    <Text size="sm" c="dimmed">
                      Showing {(page - 1) * ITEMS_PER_PAGE + 1}-
                      {Math.min(page * ITEMS_PER_PAGE, filteredData.length)} of{" "}
                      {filteredData.length}
                    </Text>
                    <Pagination
                      total={totalPages}
                      value={page}
                      onChange={setPage}
                      size="sm"
                    />
                  </Group>
                )}
              </Group>

              {/* Table */}
              {isLoading ? (
                <Center p="xl">
                  <Loader />
                </Center>
              ) : filteredData.length === 0 ? (
                <Text c="dimmed" ta="center" py="xl">
                  {hasActiveFilters || search
                    ? "No ICD codes match the current filters"
                    : "No ICD codes found"}
                </Text>
              ) : (
                <Table striped highlightOnHover>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th style={{ width: 140 }}>Code</Table.Th>
                      <Table.Th>Description</Table.Th>
                      <Table.Th style={{ width: 100 }}>Status</Table.Th>
                      <Table.Th style={{ width: 100 }}>Actions</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {paginatedData.map((item) => (
                      <Table.Tr
                        key={item.id}
                        style={{ opacity: item.isActive ? 1 : 0.5 }}
                      >
                        <Table.Td>
                          <Text size="sm" fw={600} ff="monospace">
                            {item.code}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm" lineClamp={2}>
                            {item.description || "\u2014"}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <Badge
                            color={item.isActive ? "green" : "red"}
                            variant="light"
                          >
                            {item.isActive ? "Active" : "Inactive"}
                          </Badge>
                        </Table.Td>
                        <Table.Td>
                          <Group gap="xs">
                            <Tooltip label="Edit">
                              <ActionIcon
                                variant="subtle"
                                color="blue"
                                onClick={() => handleEdit(item)}
                              >
                                <IconEdit size={18} />
                              </ActionIcon>
                            </Tooltip>
                            <Tooltip
                              label={item.isActive ? "Deactivate" : "Activate"}
                            >
                              <ActionIcon
                                variant="subtle"
                                color={item.isActive ? "red" : "green"}
                                onClick={() => handleToggleActive(item)}
                              >
                                {item.isActive ? (
                                  <IconX size={18} />
                                ) : (
                                  <IconCheck size={18} />
                                )}
                              </ActionIcon>
                            </Tooltip>
                          </Group>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              )}
            </Stack>
          </Paper>
        </Stack>

        {/* Create/Edit Modal */}
        <Modal
          opened={formModalOpened}
          onClose={() => {
            closeFormModal();
            setEditing(null);
          }}
          title={editing ? "Edit ICD Code" : "Add New ICD Code"}
          size="md"
        >
          <Stack gap="md">
            <IcdCodeForm
              key={editing?.id ?? (formModalOpened ? "new" : "closed")}
              initialValues={editing || undefined}
              onSubmit={handleSubmit}
            />
            <Group justify="flex-end" mt="md">
              <Button
                variant="subtle"
                onClick={() => {
                  closeFormModal();
                  setEditing(null);
                }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                form="icd-code-form"
                loading={createIcdCode.isPending || updateIcdCode.isPending}
              >
                {editing ? "Update" : "Create"}
              </Button>
            </Group>
          </Stack>
        </Modal>

        {/* Filter Modal */}
        <Modal
          opened={filterModalOpened}
          onClose={closeFilterModal}
          title="Filter ICD Codes"
          size="md"
          closeOnClickOutside={false}
        >
          <Stack gap="md">
            <MultiSelect
              label="Status"
              placeholder="Select status..."
              data={[
                { value: "active", label: "Active" },
                { value: "inactive", label: "Inactive" },
              ]}
              value={pendingFilters.statusFilter || []}
              onChange={(values) =>
                setPendingFilters({
                  ...pendingFilters,
                  statusFilter: values.length > 0 ? values : undefined,
                })
              }
              clearable
            />

            <Group justify="flex-end" mt="md">
              <Button variant="subtle" onClick={handleClearPendingFilters}>
                Clear All
              </Button>
              <Button onClick={handleApplyFilters}>Apply Filters</Button>
            </Group>
          </Stack>
        </Modal>

        {/* Toggle Active Confirmation Modal */}
        <Modal
          opened={confirmModalOpened}
          onClose={() => {
            closeConfirmModal();
            setItemToToggle(null);
          }}
          title={
            itemToToggle?.isActive ? "Deactivate ICD Code" : "Activate ICD Code"
          }
          size="sm"
          centered
        >
          <Stack gap="md">
            <Text>
              Are you sure you want to{" "}
              {itemToToggle?.isActive ? "deactivate" : "activate"} ICD code{" "}
              <strong>{itemToToggle?.code}</strong>?
            </Text>
            {itemToToggle?.isActive && (
              <Text size="sm" c="dimmed">
                Deactivated codes will no longer be available for selection in
                clinical history, diagnoses, or pathologist review.
              </Text>
            )}
            <Group justify="flex-end" mt="md">
              <Button
                variant="subtle"
                onClick={() => {
                  closeConfirmModal();
                  setItemToToggle(null);
                }}
              >
                Cancel
              </Button>
              <Button
                color={itemToToggle?.isActive ? "red" : "green"}
                onClick={confirmToggleActive}
                loading={toggleIcdCodeActive.isPending}
              >
                {itemToToggle?.isActive ? "Deactivate" : "Activate"}
              </Button>
            </Group>
          </Stack>
        </Modal>
      </Box>
    </Container>
  );
}
